import React, { useState, useEffect, useRef } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { askGemini } from '../../config/gemini';
import Sidebar from '../Shared/Sidebar';

export default function AIAssistant() {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      text: `Hello! 👋 I'm the Iyanna Foundation AI Assistant. I can tell you about our students, families, programs and impact. What would you like to know?`,
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [programData, setProgramData] = useState(null);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchProgramData();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  async function fetchProgramData() {
    try {
      const studentsSnap = await getDocs(collection(db, 'students'));
      const parentsSnap = await getDocs(collection(db, 'parents'));
      const sessionsSnap = await getDocs(collection(db, 'sessions'));
      const reportsSnap = await getDocs(collection(db, 'reports'));

      const students = studentsSnap.docs.map(d => ({
        id: d.id, ...d.data()
      }));
      const parents = parentsSnap.docs.map(d => ({
        id: d.id, ...d.data()
      }));
      const sessions = sessionsSnap.docs.map(d => ({
        id: d.id, ...d.data()
      }));

      setProgramData({
        totalStudents: students.length,
        totalParents: parents.length,
        totalSessions: sessions.length,
        totalReports: reportsSnap.size,
        studentsWithCerts: students.filter(s =>
          s.hasBirthCertificate === 'yes'
        ).length,
        studentsNeedCerts: students.filter(s =>
          s.hasBirthCertificate === 'no'
        ).length,
        microfinanceParents: parents.filter(p =>
          p.capitalAmount
        ).length,
        avgAttendance: sessions.length > 0
          ? Math.round(
              sessions.reduce((sum, s) =>
                sum + (s.attendance?.totalPresent || 0), 0
              ) / sessions.length
            )
          : 0,
        topStudents: students
          .filter(s => s.progress?.academic)
          .sort((a, b) =>
            (b.progress?.academic || 0) - (a.progress?.academic || 0)
          )
          .slice(0, 3)
          .map(s => `${s.firstName} (${s.progress?.academic}%)`),
        studentsByClass: [1,2,3,4,5,6,7].map(c => ({
          class: c,
          count: students.filter(s => s.class === String(c)).length
        })).filter(c => c.count > 0),
        familyStories: parents
          .filter(p => p.familyStory?.now)
          .slice(0, 3)
          .map(p => ({
            name: p.firstName,
            story: p.familyStory?.now?.slice(0, 200)
          })),
      });
    } catch (err) {
      console.error(err);
    }
  }

  async function handleSend() {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, {
      role: 'user', text: userMessage
    }]);
    setLoading(true);

    try {
      const dataContext = programData ? `
CURRENT PROGRAM DATA (as of today):
- Total children mentored: ${programData.totalStudents}
- Total families supported: ${programData.totalParents}
- Saturday sessions held: ${programData.totalSessions}
- Staff reports submitted: ${programData.totalReports}
- Average session attendance: ${programData.avgAttendance} students
- Children with birth certificates: ${programData.studentsWithCerts}
- Children needing birth certificates: ${programData.studentsNeedCerts}
- Parents with microfinance: ${programData.microfinanceParents}
- Top performing students: ${programData.topStudents.join(', ') || 'Data being collected'}
- Students by class: ${programData.studentsByClass.map(c => `Class ${c.class}: ${c.count}`).join(', ')}

RECENT FAMILY SUCCESS STORIES:
${programData.familyStories.map(s => `${s.name}: ${s.story}`).join('\n') || 'Stories being collected'}
` : 'Program data is being loaded...';

      const conversationHistory = messages
        .slice(-6)
        .map(m => `${m.role === 'user' ? 'Donor' : 'Assistant'}: ${m.text}`)
        .join('\n');

      const prompt = `
You are the Iyanna Foundation AI Assistant, helping donors understand and engage with our NGO's work in Tanzania.

ABOUT IYANNA FOUNDATION:
- We mentor primary school children Classes 1 to 7 in Tanzania
- Motto: "Protect & Educate"
- We provide: school fees, uniforms, food, stationery, life skills sessions every Saturday at 2PM
- We teach chess to children
- We support parents with microfinance and Reflective Approach sessions
- We help families get birth certificates
- Our goal: transform children into future community leaders

${dataContext}

CONVERSATION SO FAR:
${conversationHistory}

Donor's question: ${userMessage}

Respond as a warm, knowledgeable assistant who:
- Answers questions about the program with real data
- Shares inspiring stories and impact
- Encourages donations when appropriate
- Explains how donations are used
- Is enthusiastic about the foundation's work
- Keeps responses concise but informative (2-4 paragraphs max)
- IMPORTANT — child safety: only ever use first names, never full/last names, and never claim to have or describe photos of any child or family. If asked for a child's full name, surname, exact location, or a photo, politely explain that we protect children's privacy and share only first names and general progress.
`;

      const result = await askGemini(prompt);
      setMessages(prev => [...prev, {
        role: 'assistant', text: result
      }]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: 'I apologize, I am having trouble connecting right now. Please try again in a moment! '
      }]);
    }
    setLoading(false);
  }

  const quickQuestions = [
    'How many children are you helping?',
    'How are the students progressing?',
    'How is my donation used?',
    'Tell me a success story',
    'What happens in Saturday sessions?',
    'How does microfinance help families?',
  ];

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              AI Assistant
            </div>
            <div style={styles.pageSub}>
              Ask me anything about Iyanna Foundation
            </div>
          </div>
        </div>

        <div style={styles.chatContainer}>

          {/* CHAT MESSAGES */}
          <div style={styles.messages}>
            {messages.map((msg, i) => (
              <div
                key={i}
                style={{
                  ...styles.messageRow,
                  justifyContent: msg.role === 'user'
                    ? 'flex-end'
                    : 'flex-start'
                }}
              >
                {msg.role === 'assistant' && (
                  <div style={styles.botAvatar}></div>
                )}
                <div style={{
                  ...styles.bubble,
                  ...(msg.role === 'user'
                    ? styles.userBubble
                    : styles.botBubble)
                }}>
                  {msg.text}
                </div>
                {msg.role === 'user' && (
                  <div style={styles.userAvatar}></div>
                )}
              </div>
            ))}

            {loading && (
              <div style={styles.messageRow}>
                <div style={styles.botAvatar}></div>
                <div style={styles.typingBubble}>
                  <span style={styles.dot} />
                  <span style={styles.dot} />
                  <span style={styles.dot} />
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* QUICK QUESTIONS */}
          <div style={styles.quickQuestions}>
            <div style={styles.quickLabel}>
              Quick Questions:
            </div>
            <div style={styles.quickBtns}>
              {quickQuestions.map((q, i) => (
                <button
                  key={i}
                  style={styles.quickBtn}
                  onClick={() => {
                    setInput(q);
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>

          {/* INPUT */}
          <div style={styles.inputRow}>
            <input
              style={styles.chatInput}
              placeholder="Ask me about our students, impact, donations..."
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={e => {
                if (e.key === 'Enter') handleSend();
              }}
            />
            <button
              style={styles.sendBtn}
              onClick={handleSend}
              disabled={loading || !input.trim()}
            >
              {loading ? '⟳' : '➤'}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
  },
  pageHeader: {
    marginBottom: '20px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  chatContainer: {
    background: '#fff',
    borderRadius: '20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    display: 'flex',
    flexDirection: 'column',
    height: 'calc(100vh - 180px)',
    overflow: 'hidden',
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
  },
  messageRow: {
    display: 'flex',
    alignItems: 'flex-end',
    gap: '10px',
  },
  botAvatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    flexShrink: 0,
  },
  userAvatar: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    flexShrink: 0,
  },
  bubble: {
    maxWidth: '70%',
    padding: '12px 16px',
    borderRadius: '16px',
    fontSize: '13.5px',
    lineHeight: 1.6,
    whiteSpace: 'pre-wrap',
  },
  botBubble: {
    background: '#F3F4F6',
    color: '#1C1C1E',
    borderBottomLeftRadius: '4px',
  },
  userBubble: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    borderBottomRightRadius: '4px',
  },
  typingBubble: {
    background: '#F3F4F6',
    borderRadius: '16px',
    borderBottomLeftRadius: '4px',
    padding: '14px 18px',
    display: 'flex',
    gap: '4px',
    alignItems: 'center',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: '#9CA3AF',
    animation: 'bounce 1s infinite',
  },
  quickQuestions: {
    padding: '12px 20px',
    borderTop: '1px solid #F3F4F6',
  },
  quickLabel: {
    fontSize: '11px',
    color: '#6B7280',
    marginBottom: '8px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  quickBtns: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  quickBtn: {
    padding: '6px 12px',
    borderRadius: '20px',
    border: '1.5px solid #E5E7EB',
    background: 'transparent',
    color: '#374151',
    fontSize: '11.5px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.15s',
  },
  inputRow: {
    display: 'flex',
    gap: '10px',
    padding: '16px 20px',
    borderTop: '1px solid #F3F4F6',
  },
  chatInput: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '12px',
    border: '1.5px solid #E5E7EB',
    background: '#F9FAFB',
    fontFamily: 'inherit',
    fontSize: '13.5px',
    color: '#1C1C1E',
    outline: 'none',
  },
  sendBtn: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    fontSize: '18px',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
};